import React, { useState, useEffect, useCallback } from 'react';
import { channelsAPI, usersAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Hash, Lock, Loader2, Save, UserPlus, X, Shield, Users, Archive, Check,
} from 'lucide-react';
import { toast } from 'sonner';

export function ChannelSettingsDrawer({ open, onOpenChange, channel, onUpdated, onArchived }) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Members
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);
  const [addingMember, setAddingMember] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedAddIds, setSelectedAddIds] = useState([]);

  // Archive confirm
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  const isAdmin = members.some(m => m.user_id === user?.id && m.role === 'admin');

  // Populate form when channel changes
  useEffect(() => {
    if (channel && open) {
      setName(channel.name);
      setDescription(channel.description || '');
      setIsPrivate(channel.is_private);
      setDirty(false);
      loadMembers();
    }
  }, [channel?.id, open]);

  const loadMembers = useCallback(async () => {
    if (!channel) return;
    setMembersLoading(true);
    try {
      const [memRes, usersRes] = await Promise.all([
        channelsAPI.listMembers(channel.id),
        usersAPI.list(),
      ]);
      setMembers(memRes.data || []);
      setTeamUsers((usersRes.data || []).filter(u => u.is_active));
    } catch {
      toast.error('Failed to load members');
    } finally {
      setMembersLoading(false);
    }
  }, [channel?.id]);

  // Track dirty
  useEffect(() => {
    if (!channel) return;
    const changed = name !== channel.name
      || description !== (channel.description || '')
      || isPrivate !== channel.is_private;
    setDirty(changed);
  }, [name, description, isPrivate, channel]);

  const handleSave = async () => {
    if (!channel || !dirty) return;
    setSaving(true);
    try {
      const payload = {};
      if (name !== channel.name) payload.name = name.trim();
      if (description !== (channel.description || '')) payload.description = description.trim();
      if (isPrivate !== channel.is_private) payload.is_private = isPrivate;
      const res = await channelsAPI.update(channel.id, payload);
      toast.success('Channel updated');
      setDirty(false);
      onUpdated?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update channel');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!channel) return;
    setArchiving(true);
    try {
      await channelsAPI.archive(channel.id);
      toast.success(`#${channel.name} archived`);
      setArchiveOpen(false);
      onOpenChange(false);
      onArchived?.(channel.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to archive channel');
    } finally {
      setArchiving(false);
    }
  };

  const handleAddMembers = async () => {
    if (selectedAddIds.length === 0) return;
    setAddingMember(true);
    try {
      await channelsAPI.addMembers(channel.id, selectedAddIds);
      toast.success(`Added ${selectedAddIds.length} member${selectedAddIds.length > 1 ? 's' : ''}`);
      setSelectedAddIds([]);
      setShowAddMember(false);
      loadMembers();
      onUpdated?.({ ...channel, member_count: (channel.member_count || 0) + selectedAddIds.length });
    } catch {
      toast.error('Failed to add members');
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (userId === user.id) {
      toast.error('Use "Leave Channel" instead of removing yourself');
      return;
    }
    try {
      await channelsAPI.removeMember(channel.id, userId);
      toast.success(`${userName} removed`);
      loadMembers();
      onUpdated?.({ ...channel, member_count: Math.max(0, (channel.member_count || 1) - 1) });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await channelsAPI.updateMemberRole(channel.id, userId, newRole);
      toast.success('Role updated');
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleLeave = async () => {
    try {
      await channelsAPI.removeMember(channel.id, user.id);
      toast.success(`Left #${channel.name}`);
      onOpenChange(false);
      onArchived?.(channel.id); // reuse — removes from list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave channel');
    }
  };

  const memberIds = new Set(members.map(m => m.user_id));
  const nonMembers = teamUsers.filter(u => !memberIds.has(u.id));

  if (!channel) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="channel-settings-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 font-heading">
              {isPrivate ? <Lock className="w-5 h-5" /> : <Hash className="w-5 h-5" />}
              Channel Settings
            </SheetTitle>
            <SheetDescription>Manage #{channel.name}</SheetDescription>
          </SheetHeader>

          <div className="space-y-6 mt-6">
            {/* ── Name & Description ── */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="settings-name">Channel Name</Label>
                <Input
                  id="settings-name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  maxLength={100}
                  disabled={!isAdmin}
                  className="mt-1"
                  data-testid="settings-channel-name"
                />
              </div>
              <div>
                <Label htmlFor="settings-desc">Description</Label>
                <Input
                  id="settings-desc"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  maxLength={500}
                  disabled={!isAdmin}
                  placeholder="What is this channel about?"
                  className="mt-1"
                  data-testid="settings-channel-description"
                />
              </div>

              {/* Privacy toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    {isPrivate ? <Lock className="w-4 h-4 text-muted-foreground" /> : <Hash className="w-4 h-4 text-muted-foreground" />}
                    <Label htmlFor="settings-private" className="cursor-pointer">
                      {isPrivate ? 'Private Channel' : 'Public Channel'}
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground ml-6 mt-0.5">
                    {isPrivate
                      ? 'Only invited members can view and post'
                      : 'Visible to all eligible team members — anyone can join'}
                  </p>
                </div>
                <Switch
                  id="settings-private"
                  checked={isPrivate}
                  onCheckedChange={setIsPrivate}
                  disabled={!isAdmin}
                  data-testid="settings-private-toggle"
                />
              </div>

              {/* Save button */}
              {isAdmin && (
                <Button onClick={handleSave} disabled={!dirty || saving} className="w-full" data-testid="settings-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              )}
            </div>

            {/* ── Members ── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                  <Users className="w-4 h-4" />Members ({members.length})
                </h3>
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setShowAddMember(!showAddMember); setSelectedAddIds([]); }}
                    data-testid="settings-add-member-btn"
                  >
                    <UserPlus className="w-3.5 h-3.5 mr-1" />Add
                  </Button>
                )}
              </div>

              {/* Add member panel */}
              {showAddMember && isAdmin && (
                <div className="border rounded-lg p-3 mb-3 bg-muted/30 space-y-2" data-testid="add-member-panel">
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {nonMembers.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">All team members are in this channel</p>
                    ) : nonMembers.map(u => (
                      <div
                        key={u.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors hover:bg-muted ${
                          selectedAddIds.includes(u.id) ? 'bg-primary/10' : ''
                        }`}
                        onClick={() => setSelectedAddIds(prev =>
                          prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id]
                        )}
                        data-testid={`add-member-option-${u.id}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] ${
                          selectedAddIds.includes(u.id)
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-input'
                        }`}>
                          {selectedAddIds.includes(u.id) && <Check className="w-3 h-3" />}
                        </div>
                        <span className="text-sm">{u.name}</span>
                        <span className="text-xs text-muted-foreground capitalize ml-auto">{u.role}</span>
                      </div>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={selectedAddIds.length === 0 || addingMember}
                    onClick={handleAddMembers}
                    data-testid="add-member-confirm-btn"
                  >
                    {addingMember ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                    Add {selectedAddIds.length > 0 ? selectedAddIds.length : ''} Member{selectedAddIds.length !== 1 ? 's' : ''}
                  </Button>
                </div>
              )}

              {/* Member list */}
              {membersLoading ? (
                <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin" /></div>
              ) : (
                <div className="space-y-1" data-testid="member-list">
                  {members.map(m => {
                    const isMe = m.user_id === user.id;
                    const isCreator = m.user_id === channel.created_by;
                    return (
                      <div key={m.id} className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted/50 group" data-testid={`member-row-${m.user_id}`}>
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0 text-primary text-xs font-bold">
                          {m.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium truncate">{m.user_name}</span>
                            {isMe && <span className="text-[10px] text-muted-foreground">(you)</span>}
                          </div>
                          <span className="text-xs text-muted-foreground">{m.user_email}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {m.role === 'admin' ? (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              <Shield className="w-3 h-3 mr-0.5" />Admin
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">Member</Badge>
                          )}
                          {/* Actions — only for admins, not on themselves, not on creator */}
                          {isAdmin && !isMe && !isCreator && (
                            <div className="hidden group-hover:flex items-center gap-0.5">
                              <button
                                className="p-1 rounded text-muted-foreground hover:text-primary transition-colors"
                                title={m.role === 'admin' ? 'Demote to member' : 'Promote to admin'}
                                onClick={() => handleRoleChange(m.user_id, m.role === 'admin' ? 'member' : 'admin')}
                                data-testid={`toggle-role-${m.user_id}`}
                              >
                                <Shield className="w-3.5 h-3.5" />
                              </button>
                              <button
                                className="p-1 rounded text-muted-foreground hover:text-destructive transition-colors"
                                title="Remove from channel"
                                onClick={() => handleRemoveMember(m.user_id, m.user_name)}
                                data-testid={`remove-member-${m.user_id}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ── Danger Zone ── */}
            <div className="border-t pt-4 space-y-2">
              <h3 className="text-xs font-bold uppercase tracking-wide text-destructive">Danger Zone</h3>
              {!members.some(m => m.user_id === user.id && m.user_id === channel.created_by) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700"
                  onClick={handleLeave}
                  data-testid="settings-leave-btn"
                >
                  Leave Channel
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-destructive border-destructive/30 hover:bg-destructive/10"
                  onClick={() => setArchiveOpen(true)}
                  data-testid="settings-archive-btn"
                >
                  <Archive className="w-3.5 h-3.5 mr-1.5" />Archive Channel
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Archive Confirmation */}
      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent data-testid="archive-channel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive #{channel.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will hide the channel from the sidebar for all members. Messages and history are preserved and can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchive}
              disabled={archiving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="archive-channel-confirm"
            >
              {archiving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Archiving...</> : 'Archive Channel'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
